import type { NineSliceBorder } from "./tokens.js";

// Phaser-free subpath(`@yjsgames/phaser-ui/panel-content`) 소비자가 NineSliceBorder를 함께 받도록 re-export
// (tokens.ts는 `import type Phaser`라 값 로드 0 — 이 진입점 전체가 Phaser 비의존).
export type { NineSliceBorder } from "./tokens.js";

/** 사각 영역 (좌상단 x/y + 크기). 패널/콘텐츠 영역 공통 표현. */
export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * 패널 콘텐츠 inset 사양 — 9-slice 테두리 + 내부 여백.
 * `PanelStyleToken`(border: NineSliceBorder, contentPadding)과 호환되며, 콘텐츠를 더 안으로 넣는
 * `innerPadX/Y`를 선택적으로 더한다. 4방향 변별을 위해 border는 단일 number가 아닌 `NineSliceBorder`.
 */
export interface PanelContentSpec {
  /** 9-slice 테두리 픽셀 (좌/우/상/하 변별). 보통 PanelStyleToken.border 그대로. */
  readonly border: NineSliceBorder;
  /** 테두리 안쪽 콘텐츠 여유 padding (4방향 균일). 보통 PanelStyleToken.contentPadding. */
  readonly contentPadding: number;
  /** 가로 추가 여백 (좌우 각각). default 0. */
  readonly innerPadX?: number;
  /** 세로 추가 여백 (상하 각각). default 0. */
  readonly innerPadY?: number;
}

/**
 * NineSlice 패널의 외곽 사각형(`panelRect`)에서 콘텐츠가 안전하게 배치될 안쪽 영역을 계산한다.
 * Phaser·도메인 의존 0 순수 함수 — 같은 9-slice 패널을 쓰는 모든 뷰가 이 한 계약으로 동일 inset을 공유해
 * 테두리 침범을 구조적으로 차단한다(뷰별 magic-number padding 복붙 제거).
 *
 * inset(각 변) = border[변] + contentPadding + innerPad. 4방향을 변별 계산하므로 비대칭 9-slice도 정확.
 *
 * 사용 예:
 * ```ts
 * const content = resolvePanelContentRect(
 *   { x: 10, y: 425, width: 340, height: 327 },
 *   { border: style.border, contentPadding: style.contentPadding, innerPadX: 8, innerPadY: 2 },
 * );
 * // content 안에서만 자식 요소 배치
 * ```
 */
export function resolvePanelContentRect(panelRect: Rect, spec: PanelContentSpec): Rect {
  const padX = spec.innerPadX ?? 0;
  const padY = spec.innerPadY ?? 0;
  const insetL = spec.border.left + spec.contentPadding + padX;
  const insetR = spec.border.right + spec.contentPadding + padX;
  const insetT = spec.border.top + spec.contentPadding + padY;
  const insetB = spec.border.bottom + spec.contentPadding + padY;
  return {
    x: panelRect.x + insetL,
    y: panelRect.y + insetT,
    width: panelRect.width - insetL - insetR,
    height: panelRect.height - insetT - insetB,
  };
}

// 레이아웃 엔진(stack/row/grid) — 같은 Phaser-free 진입점(`/panel-content`)으로 함께 노출.
export {
  resolveVerticalStack,
  resolveRow,
  resolveGrid,
  type StackChild,
  type VerticalStackSpec,
  type RowItem,
  type GridSpec,
} from "./StackLayout.js";

// RTL 좌표 미러 순수 유틸 — grid RTL 레이아웃 계산에서 함께 쓰도록 같은 Phaser-free 진입점으로 노출.
export { mirrorX, mirrorCenterX, mirrorAnchorX } from "./mirror.js";
